/**
 * SupportedService
 *
 * Service for fetching supported configuration from the backend /api/v1/supported/* endpoints.
 * Includes in-memory caching. Backend DB is the single source of truth.
 */

// Environment-based URL selection
const ENV = process.env.REACT_APP_ENV || 'local';
const ENV_URLS = {
    local: process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3001',
    development: process.env.REACT_APP_DEV_API_URL || 'https://betagate.kubercrypto.io',
    staging: process.env.REACT_APP_STAGING_API_URL || 'https://betagate.kubercrypto.io',
    production: process.env.REACT_APP_PROD_API_URL || 'https://apigate.kubercrypto.io',
};
const API_BASE_URL = ENV_URLS[ENV] || ENV_URLS.local;

// Debug logging
const DEBUG = process.env.REACT_APP_ENABLE_DEBUG === 'true';
const log = (...args) => DEBUG && console.log('[SupportedService]', ...args);

// Log current environment on init
if (DEBUG) {
    console.log(`[SupportedService] ENV: ${ENV}, API: ${API_BASE_URL}`);
}

// No hardcoded fallbacks — backend DB is the single source of truth.
// If API fails, return empty arrays so the UI shows a proper error state.

class SupportedService {
    constructor() {
        this.cache = {};
        this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    }

    /**
     * Check if cache is still valid
     */
    isCacheValid(key) {
        const cached = this.cache[key];
        if (!cached) return false;
        return Date.now() - cached.timestamp < this.cacheExpiry;
    }

    /**
     * Fetch from API with caching. Returns empty array on failure.
     */
    async fetchFromApi(endpoint, cacheKey) {
        // Return from cache if valid
        if (this.isCacheValid(cacheKey)) {
            log(`Cache hit for ${cacheKey}`);
            return this.cache[cacheKey].data;
        }

        try {
            log(`Fetching ${API_BASE_URL}${endpoint}`);
            const response = await fetch(`${API_BASE_URL}${endpoint}`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const json = await response.json();
            // API returns { data: { message: [...] }, message: "...", status: 200 }
            // Extract the actual data array from json.data.message or json.data
            const data = json.data?.message || json.data || json.message || json;

            // Store in cache
            this.cache[cacheKey] = {
                data,
                timestamp: Date.now(),
            };

            log(`Fetched ${cacheKey}:`, data.length || Object.keys(data).length, 'items');
            return data;
        } catch (error) {
            console.warn(`Failed to fetch ${endpoint}:`, error.message);
            return [];
        }
    }

    /**
     * Get supported networks
     * @param {string} [context] - 'widget' for all networks, 'payment' for gateway networks only (bsc, ethereum, tron, solana)
     */
    async getNetworks(context) {
        const endpoint = context
            ? `/api/v1/supported/networks?context=${context}`
            : '/api/v1/supported/networks';
        const cacheKey = context ? `networks_${context}` : 'networks';
        return this.fetchFromApi(endpoint, cacheKey);
    }

    /**
     * Get supported tokens
     * @param {string} [context] - 'widget' for all tokens, 'payment' for stablecoins on gateway networks only
     * @param {string} [fiat] - Optional fiat currency to filter tokens by eligible providers
     */
    async getTokens(context, fiat) {
        const params = [];
        if (context) params.push(`context=${context}`);
        if (fiat) params.push(`fiat=${fiat}`);
        const endpoint = params.length
            ? `/api/v1/supported/tokens?${params.join('&')}`
            : '/api/v1/supported/tokens';
        const cacheKey = `tokens_${context || 'all'}_${fiat || 'all'}`;
        return this.fetchFromApi(endpoint, cacheKey);
    }

    /**
     * Get supported fiat currencies
     */
    async getFiatCurrencies() {
        return this.fetchFromApi('/api/v1/supported/fiat', 'fiats');
    }

    /**
     * Get supported payment methods
     * @param {string} [fiat] - Optional fiat currency code to filter methods (e.g., 'INR', 'USD')
     */
    async getPaymentMethods(fiat) {
        const url = fiat
            ? `/api/v1/supported/payment-methods?fiat=${fiat}`
            : '/api/v1/supported/payment-methods';
        const cacheKey = fiat ? `paymentMethods_${fiat}` : 'paymentMethods';
        return this.fetchFromApi(url, cacheKey);
    }

    /**
     * Get all onramps with icons
     */
    async getAllOnramps() {
        return this.fetchFromApi('/api/v1/supported/onramps/all', 'allOnramps');
    }

}

// Export singleton instance
const supportedService = new SupportedService();
export default supportedService;
